




































































































import Vue from 'vue';
import Component from 'vue-class-component';
import { InjectReactive } from 'vue-property-decorator';

// Shared types
import { System, Brand, Color, IndoorUnit, OutdoorUnit, IndoorUnitColor } from '@coldtec/cos-types';

// Store types
import { ToolSystem } from '@/store/store.types';

// Component that is used to add an accordion item
import AccordionItem from './SystemAccordionItem.vue';

// Components for each item of the accordion
import SystemUnits from '../units/SystemUnits.vue';
import SystemDescription from '../SystemDescription.vue';
import SystemAccessories from '../accessories/SystemAccessories.vue';

// Used to preview the available colors the including units
import SystemColors from '../SystemColors.vue';

/**
 *  A section of the accordion.
 */
export enum Section {
    Units = 'units',
    Description = 'description',
    Accessories = 'accessories',
}

/**
 *  Holding the open states of the sections.
 */
interface SectionOpened {
    [key: string]: boolean;
}

/**
 *  Holding the data of the including units.
 */
interface Units {
    /** The total count of including units. */
    count: number;

    /** All the available colors of all the including indoor units. */
    availableColors: Array<Color>;
}

/**
 *  Holding the static guid of the white color, so it
 *  can easily and securely be found to prevent the white
 *  color from being added to the available colors Array.
 */
const WHITE_COLOR_STATIC_GUID = 'ccos-static-guid-color-white-ccos-st';

/**
 *  Accordion component that holds the included units, description and accessories of a system.
 */
@Component({
    components: {
        AccordionItem,
        SystemUnits,
        SystemDescription,
        SystemAccessories,
        SystemColors,
    },
})
export default class SystemAccordion extends Vue {

    public $refs!: {
        accordion: SystemAccordion;
        [Section.Units]: AccordionItem;
        [Section.Description]: AccordionItem;
        [Section.Accessories]: AccordionItem;
    };

    @InjectReactive('system')
    private system!: System;

    @InjectReactive('toolSystem')
    private toolSystem!: ToolSystem;

    /**
     *  Holding the open states of every section.
     */
    private open: SectionOpened = {
        [Section.Units]: false,
        [Section.Description]: false,
        [Section.Accessories]: false,
    };

    /**
     *  Forward the section enum to allow the template to use it.
     */
    private Section = Section;

    /**
     *  Holding the data of the including units.
     */
    private units: Units = {
        count: 0,
        availableColors: [],
    }

    /**
     *  Used to get the name of the system, which is
     *  a combination of the brand name and SKU.
     */
    private get systemName(): string {

        return `${ (this.system.brand as Brand).name } ${ this.system.sku }`;

    }

    /**
     *  The following getters are used to get the title of the accessories'
     *  toggle actions, based on the their current open state.
     */
    private get unitsToggleTitle(): string {

        return `${ this._getToggleAction(Section.Units) } de inbegrepen units van de ${ this.systemName }`;

    }

    private get descriptionToggleTitle(): string {

        return `${ this._getToggleAction(Section.Description) } de omschrijving van de ${ this.systemName }`;

    }

    private get accessoriesToggleTitle(): string {

        return `${ this._getToggleAction(Section.Accessories) } de accessoires van de ${ this.systemName }`;

    }

    /**
     *  Used to get the count of selected accessories of the
     *  system for which the accordion is rendered.
     */
    private get addedAccessoriesCount(): number {

        let count = 0;

        for (const accessory of this.toolSystem.accessories) {

            if (accessory.selected) {

                count++;

            }

        }

        return count;

    }

    /**
     *  Sum the amount of including units and create
     *  the list of all available colors of all including
     *  indoor units to preview.
     */
    private created(): void {

        for (const unitsProp of [ 'outdoor_units', 'indoor_units' ] as Array<keyof System>) {

            const units = (this.system[unitsProp] || []) as Array<IndoorUnit | OutdoorUnit>;

            /**
             *  Add the amount of the units.
             */
            this.units.count += units.length;

            /**
             *  Only check the colors of the indoor units.
             */
            if (unitsProp !== 'indoor_units') {

                continue;

            }

            /**
             *  Add every available indoor unit color (except for the white).
             */
            for (const indoorUnit of units as Array<IndoorUnit>) {

                for (const color of (indoorUnit.colors || []) as Array<IndoorUnitColor>) {

                    /**
                     *  Don't add the white color because it's clear to everyone
                     *  that an indoor unit is usually white and the white color
                     *  square looks like a checkbox in this context.
                     */
                    if (color.color.guid === WHITE_COLOR_STATIC_GUID) {

                        continue;

                    }

                    /**
                     *  Ensure the color is not already added, because
                     *  showing duplicate colors might confuse the user
                     *  and lets Vue throw a `duplicate key` error.
                     */
                    let alreadyAdded = false;

                    for (const checkColor of this.units.availableColors) {

                        if (checkColor.guid !== color.color.guid) {

                            continue;

                        }

                        alreadyAdded = true;

                        break;

                    }

                    if (alreadyAdded) {

                        continue;

                    }

                    this.units.availableColors.push(color.color);

                }

            }

        }

    }

    /**
     *
     *  Used to open and highlight the forwarded
     *  section of the accordion.
     *
     *  (i) Only scrolls the section into view and highlights
     *  the section in case the section is already open
     *
     *  @param section The section to open and highlight
     *
     */
    public openAndHighlight(section: Section): void {

        /**
         *  Get the section's item.
         */
        const item: AccordionItem | undefined = this.$refs[section];

        if (!item) {

            return;

        }

        /**
         *  Ensure the section is opened if not already open.
         */
        if (!this.open[section]) {

            this.toggle(section);

        }

        /**
         *  Give the DOM some time to close the other section and open
         *  this one before scrolling this one into view, needed to
         *  prevent the browser from jumpy behaviour between sections.
         */
        setTimeout(() => {

            item.$el.scrollIntoView({
                behavior: 'smooth',
            });

            item.highlight();

        }, 200);

    }

    /**
     *
     *  Used to toggle the open state of the forwarded section,
     *  always closing all the other sections by default.
     *
     *  @param toggleSection The name of the section to toggle
     *
     */
    private toggle(toggleSection: Section): void {

        for (const section of Object.keys(this.open) as Array<Section>) {

            if (section === toggleSection) {

                this.open[section] = !this.open[section];

                continue;

            }

            this.open[section] = false;

        }

    }

    /**
     *
     *  Used to get the action (string) of the forwarded section, based on his open state.
     *
     *  @param section The section to get the action (string) for
     *
     *  @returns The correct action string
     *
     */
    private _getToggleAction(section: Section): 'Sluit' | 'Open' {

        return this.open[section] ? 'Sluit' : 'Open';

    }

}
